/**
 * The `1v1` scene: a two-party call.
 *
 * Layout: full-screen remote video with a small local PiP, call controls (mic, camera, invite,
 * device switch, token renewal, hang up) and a real call lifecycle machine
 * (`waiting -> connected -> ended`). The audience role is rejected before a token is requested.
 */
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { DeviceService, type DeviceGrouped, type DeviceSelection } from '@nimeet/ui-kit-react';
import { buildInviteLink } from '../config';
import type { MeetingRole } from '../meeting/session';
import { describeError } from '../meeting/session';
import { useI18n } from '../i18n';
import { Banner, Modal, RemoteAudio, VideoTile, useSessionState } from '../ui';
import { initialOneToOne, reduceOneToOne } from '../scenarios/machines';
import { ConfirmModal, InviteModal, RoomHeader, useNoticeToast } from './common';
import type { RoomContext } from './types';

export function CallRoom({ context }: { context: RoomContext }) {
  const { session, client, notify, onLeave } = context;
  const { t } = useI18n();
  const state = useSessionState(
    useCallback((listener) => session.subscribe(listener), [session]),
    useCallback(() => session.getState(), [session]),
  );
  useNoticeToast(session, notify);
  const [call, dispatch] = useReducer(reduceOneToOne, undefined, initialOneToOne);
  const [dialog, setDialog] = useState<null | 'invite' | 'devices' | 'end'>(null);
  const [devices, setDevices] = useState<{
    group: DeviceGrouped;
    selection: DeviceSelection;
  } | null>(null);
  const deviceService = useMemo(() => new DeviceService(), []);

  const local = state.participants.find((entry) => entry.isLocal) ?? null;
  const remote = state.participants.find((entry) => !entry.isLocal) ?? null;
  const isHost = local?.role === 'host';
  const remoteStream = remote ? client.getRemoteStream(remote.uid, 'video') : null;
  const localStream = client.getLocalStream('camera');

  useEffect(() => {
    if (state.phase === 'joined') dispatch({ type: 'local-joined', at: Date.now() });
  }, [state.phase]);
  // 依赖用标量而非 participant 对象：对象每次 store 更新都会换引用，effect 会被重复触发。
  const remoteUid = remote?.uid ?? null;
  const remoteName = remote?.displayName ?? null;
  useEffect(() => {
    if (remoteUid === null || remoteName === null) return;
    dispatch({ type: 'remote-joined', uid: remoteUid, name: remoteName });
  }, [remoteUid, remoteName]);
  useEffect(() => {
    if (!remote && call.phase === 'connected')
      dispatch({ type: 'remote-left', uid: call.peerUid ?? -1 });
  }, [remote, call.phase, call.peerUid]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const listed = await deviceService.listDevices();
      if (!alive || !listed.ok) return;
      setDevices({ group: listed.value, selection: deviceService.getSelection() });
    })();
    return () => {
      alive = false;
      deviceService.dispose();
    };
  }, [deviceService]);

  const invite = buildInviteLink(state.channel ?? '', local?.displayName, undefined, '1v1');
  const errorText = state.errorKey ? t(state.errorKey) : state.error;
  const statusText =
    call.phase === 'connected'
      ? t('1v1.connected', { name: call.peerName ?? '' })
      : t('1v1.waiting');

  const renew = (): void => {
    void session
      .renew()
      .then(() => {
        dispatch({ type: 'token-renewed' });
        notify(t('1v1.renewed'));
      })
      .catch((error) => notify(describeError(error)));
  };

  return (
    <main className="nm-call" data-testid="scene-1v1" data-scene="1v1">
      <RoomHeader
        scene="1v1"
        channel={state.channel ?? ''}
        onLeave={() => setDialog('end')}
        meta={
          <span className="nm-badge is-live" data-testid="1v1-status">
            {statusText}
          </span>
        }
        actions={
          <button className="nm-btn" onClick={renew} data-testid="1v1-renew">
            {t('1v1.renew')}
          </button>
        }
      />
      {errorText ? (
        <Banner tone="error">
          <span data-testid="room-error">{errorText}</span>
        </Banner>
      ) : null}
      {!isHost ? (
        <Banner tone="error">
          <span data-testid="1v1-audience-denied">{t('1v1.audienceDenied')}</span>
        </Banner>
      ) : null}

      <section className="nm-call-stage">
        <div className="nm-call-remote" data-testid="1v1-remote">
          {remote ? (
            <VideoTile
              participant={remote}
              stream={remoteStream}
              spotlight
              className="nm-tile--stage"
            />
          ) : (
            <div className="nm-call-waiting" data-testid="1v1-waiting">
              <div className="nm-pulse" aria-hidden="true">
                {initials(t('1v1.waiting'))}
              </div>
              <p>{t('1v1.waiting')}</p>
              <button className="nm-btn" onClick={() => setDialog('invite')}>
                {t('1v1.invite')}
              </button>
            </div>
          )}
          <div className="nm-call-pip" data-testid="1v1-local">
            {local ? <VideoTile participant={local} stream={localStream} /> : null}
          </div>
        </div>
        {remote ? <RemoteAudio stream={client.getRemoteStream(remote.uid, 'audio')} /> : null}
      </section>

      <div
        className="nm-call-controls"
        role="toolbar"
        aria-label={t('1v1.callControls')}
        data-testid="1v1-controls"
      >
        <button
          className="nm-btn nm-call-btn"
          aria-pressed={state.micEnabled}
          disabled={!isHost}
          onClick={() => void session.toggleMic()}
          title={state.micEnabled ? t('1v1.mute') : t('1v1.unmute')}
          data-testid="1v1-mic"
        >
          {state.micEnabled ? '🎙' : '🔇'}{' '}
          <span>{state.micEnabled ? t('1v1.mute') : t('1v1.unmute')}</span>
        </button>
        <button
          className="nm-btn nm-call-btn"
          aria-pressed={state.cameraEnabled}
          disabled={!isHost}
          onClick={() => void session.toggleCamera()}
          title={state.cameraEnabled ? t('1v1.cameraOff') : t('1v1.cameraOn')}
          data-testid="1v1-camera"
        >
          {state.cameraEnabled ? '🎥' : '🚫'}{' '}
          <span>{state.cameraEnabled ? t('1v1.cameraOff') : t('1v1.cameraOn')}</span>
        </button>
        <button
          className="nm-btn nm-call-btn"
          onClick={() => setDialog('invite')}
          data-testid="1v1-invite"
        >
          ➕ <span>{t('1v1.invite')}</span>
        </button>
        <button
          className="nm-btn nm-call-btn"
          onClick={() => setDialog('devices')}
          data-testid="1v1-devices"
        >
          ⚙️ <span>{t('1v1.devices')}</span>
        </button>
        <button className="nm-btn nm-call-btn" onClick={renew} data-testid="1v1-renew-controls">
          ⟳ <span>{t('1v1.renew')}</span>
        </button>
        <button
          className="nm-btn nm-call-btn is-danger"
          onClick={() => setDialog('end')}
          data-testid="1v1-end"
        >
          ⏹ <span>{t('1v1.hangUp')}</span>
        </button>
      </div>

      {dialog === 'invite' ? (
        <InviteModal
          title={t('1v1.inviteTitle')}
          link={invite}
          note={t('1v1.inviteNote')}
          count={state.participants.length}
          channel={state.channel}
          testId="1v1-invite-dialog"
          onCopy={() => {
            void navigator.clipboard?.writeText(invite).catch(() => undefined);
            notify(t('toast.inviteCopied'));
            dispatch({ type: 'invite-created' });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'devices' ? (
        <Modal
          title={t('1v1.devicesTitle')}
          onClose={() => setDialog(null)}
          testId="1v1-devices-dialog"
        >
          <div className="nm-field">
            <span>{t('1v1.deviceCamera')}</span>
            <select
              className="nm-select"
              aria-label={t('1v1.deviceCamera')}
              data-testid="1v1-device-camera"
              value={devices?.selection.cameraId ?? ''}
              onChange={(event) => {
                void deviceService.select('camera', event.target.value || null);
                dispatch({ type: 'device-switched' });
              }}
            >
              <option value="">{t('1v1.deviceDefaultCamera')}</option>
              {(devices?.group.cameras ?? []).map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || t('join.device.cameraN', { index: index + 1 })}
                </option>
              ))}
            </select>
          </div>
          <div className="nm-field">
            <span>{t('1v1.deviceMic')}</span>
            <select
              className="nm-select"
              aria-label={t('1v1.deviceMic')}
              data-testid="1v1-device-microphone"
              value={devices?.selection.microphoneId ?? ''}
              onChange={(event) => {
                void deviceService.select('microphone', event.target.value || null);
                dispatch({ type: 'device-switched' });
              }}
            >
              <option value="">{t('1v1.deviceDefaultMic')}</option>
              {(devices?.group.microphones ?? []).map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || t('join.device.microphoneN', { index: index + 1 })}
                </option>
              ))}
            </select>
          </div>
          <div className="nm-field">
            <span>{t('1v1.deviceSpeaker')}</span>
            <select
              className="nm-select"
              aria-label={t('1v1.deviceSpeaker')}
              data-testid="1v1-device-speaker"
              defaultValue=""
            >
              <option value="">{t('1v1.deviceDefaultSpeaker')}</option>
              {(devices?.group.speakers ?? []).map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
          </div>
          <p className="nm-sub">{t('1v1.deviceNote')}</p>
        </Modal>
      ) : null}

      {dialog === 'end' ? (
        <ConfirmModal
          title={t('1v1.hangUpTitle')}
          body={
            call.phase === 'connected'
              ? t('1v1.hangUpBody', { name: call.peerName ?? '' })
              : t('1v1.hangUpBodyWaiting')
          }
          confirmLabel={t('1v1.hangUp')}
          onConfirm={() => {
            dispatch({ type: 'ended' });
            void session
              .leave()
              .catch(() => undefined)
              .finally(onLeave);
          }}
          onClose={() => setDialog(null)}
          testId="1v1-end-dialog"
        />
      ) : null}
    </main>
  );
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

export type { MeetingRole };
